import React, { useState } from "react";
import "./ImageWithOptionsOverlay.scss";

import classNames from "classnames";
import { ReactComponent as SettingsIcon } from "src/images/settings-v.svg";
import { ReactComponent as PlusIcon } from "src/images/plus-transparent.svg";
import { ReactComponent as TrashIcon } from "src/images/trash-can.svg";
import { ReactComponent as FullScreenIcon } from "src/images/resize.svg";
import { ReactComponent as ReplaceIcon } from "src/images/replace.svg";
import { ReactComponent as DownloadIcon } from "src/images/download-alt.svg";
import placeholderIcon from "src/images/placeholder-image.svg";
import { downloadUrl } from "src/app/pages/CreatorDiscoveryTool/utils/downloadUrl";
import { Dropdown, DropdownMenu, DropdownMenuItem } from "../Dropdown/Dropdown";
import IDHFormattedMessage from "../IDHFormattedMessage/IDHFormattedMessage";
import Skeleton from "../Skeleton/Skeleton";
import { Img } from "../Img/Img";
import ImageCropModal from "../CroppedImageUploader/ImageCropModal/ImageCropModal";
import { GalleryLightboxLocal } from "../GalleryLightboxLocal/GalleryLightboxLocal";

interface Props {
  imageSrc: string | null | undefined;
  downloadImageSrc?: string | undefined;
  downloadFileName?: string;
  fallbackImageSrc?: string;
  className?: string;
  size?: number;
  shape?: "circle" | "square";
  loading?: boolean;
  uploadFunction?: (file: File) => void;
  removeFunction?: () => void;
}

function ImageWithOptionsOverlay(props: Props) {
  const {
    imageSrc = undefined,
    downloadImageSrc = undefined,
    downloadFileName,
    fallbackImageSrc = placeholderIcon,
    className,
    size = 80,
    shape,
    loading,
    uploadFunction,
    removeFunction,
  } = props;

  const [showImageCropModal, setShowImageCropModal] = useState<boolean>(false);
  const [showPreview, setShowPreview] = useState<boolean>(false);

  const radius = shape === "circle" ? "50%" : "4px";

  const canUploadFirstImage = !imageSrc && uploadFunction;

  const extension = downloadImageSrc?.split(".").pop()?.split("?")[0];
  const fileNameWithExtension = `${downloadFileName || "cover"}.${extension}`;

  return (
    <div
      style={{ width: size, height: size, borderRadius: radius }}
      className={classNames("image-with-options-overlay", className)}
    >
      {loading ? (
        <Skeleton width={size} height={size} borderRadius={radius} />
      ) : (
        <Img
          className="image-with-options-overlay__image"
          src={imageSrc || undefined}
          fallbackImageSrc={fallbackImageSrc}
          style={{ width: size, height: size, borderRadius: radius }}
          loading="lazy"
          alt="image"
        />
      )}

      <Dropdown>
        {(isOpen, setIsOpen) => (
          <>
            <span
              style={{ width: size, height: size, borderRadius: radius }}
              className="image-with-options-overlay__button"
              onClick={() => {
                if (canUploadFirstImage) {
                  setShowImageCropModal(true);
                } else {
                  setIsOpen(true);
                }
              }}
            >
              {canUploadFirstImage ? <PlusIcon /> : <SettingsIcon />}
            </span>

            <DropdownMenu isOpen={isOpen}>
              {downloadImageSrc && (
                <>
                  <DropdownMenuItem onClick={() => setShowPreview(true)}>
                    <FullScreenIcon />
                    <IDHFormattedMessage
                      id="ws_preview_full_screen"
                      defaultMessage="Preview full screen"
                    />
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    onClick={() => {
                      downloadUrl(downloadImageSrc, fileNameWithExtension);
                      setIsOpen(false);
                    }}
                  >
                    <DownloadIcon />
                    <IDHFormattedMessage
                      id="ws_download_image"
                      defaultMessage="Download image"
                    />
                  </DropdownMenuItem>
                </>
              )}

              {uploadFunction && (
                <DropdownMenuItem
                  className="image-input"
                  onClick={() => setShowImageCropModal(true)}
                >
                  <ReplaceIcon />
                  <IDHFormattedMessage
                    id="ws_replace_cover"
                    defaultMessage="Replace cover"
                  />
                </DropdownMenuItem>
              )}

              {removeFunction && (
                <DropdownMenuItem
                  className="remove-button"
                  onClick={removeFunction}
                >
                  <TrashIcon fill="#D93025" stroke="D93025" />
                  <IDHFormattedMessage
                    id="ws_remove_cover"
                    defaultMessage="Remove cover"
                  />
                </DropdownMenuItem>
              )}
            </DropdownMenu>
          </>
        )}
      </Dropdown>

      {showImageCropModal && (
        <ImageCropModal
          uploadFunction={uploadFunction}
          onClose={() => setShowImageCropModal(false)}
          circle={shape === "circle"}
        />
      )}

      {showPreview && (
        <GalleryLightboxLocal
          show={showPreview}
          sources={[{ url: downloadImageSrc }]}
          hide={() => setShowPreview(false)}
        />
      )}
    </div>
  );
}

export default ImageWithOptionsOverlay;
